import {PathCommand} from './PathCommand';
import {Point} from '../Point';
import {MoveTo} from './point/MoveTo';
import {LineTo} from './line/LineTo';
import {HLineTo} from './line/oriented/HLineTo';
import {VLineTo} from './line/oriented/VLineTo';
import {CBezier} from './curve/bezier/cubic/CBezier';
import {SCBezier} from './curve/bezier/cubic/SCBezier';
import {QBezier} from './curve/bezier/quadratic/QBezier';
import {SQBezier} from './curve/bezier/quadratic/SQBezier';
import {Arc} from './curve/arc/Arc';

export class Path {
  private _commands: PathCommand[] = [];

  public constructor(commands: PathCommand[] = []) {
    this._commands = commands;
  }

  public get copy(): Path {
    const path: Path = new Path();
    this._commands.forEach((command: PathCommand) =>
      path.add(command.copy)
    );
    return path;
  }

  public get points(): Point[] {
    const points: Point[] = [];
    for (const command of this._commands) {
      points.push(command.position);
    }
    return points;
  }

  public getAll(): PathCommand[] {
    return this._commands;
  }
  public setAll(commands: PathCommand[]): void {
    this._commands = commands;
  }

  public get(index: number): PathCommand {
    if (index < 0) {
      index = this._commands.length + index;
    }

    return this._commands[index];
  }
  public set(index: number, command: PathCommand): void {
    if (index < 0) {
      index = this._commands.length + index;
    }

    this._commands[index] = command;
  }

  public add(command: PathCommand): void {
    this._commands.push(command);
  }
  public addAll(commands: PathCommand[]): void {
    commands.forEach((command: PathCommand) => {
      this._commands.push(command);
    });
  }
  public addPath(path: Path): void {
    path._commands.forEach((command: PathCommand) => {
      this._commands.push(command);
    });
  }
  public remove(index: number): void {
    if (index < 0) {
      index = this._commands.length + index;
    }

    const command: PathCommand = this._commands[index];
    this._commands = this._commands.splice(this._commands.indexOf(command), 1);
  }

  public replace(index: number, point: Point): void {
    if (index < 0) {
      index = this._commands.length + index;
    }

    this._commands[index].position = point;
  }
  public get commands(): PathCommand[] {
    return this._commands;
  }
  public replaceCommand(index: number, command: PathCommand): void {
    if (index < 0) {
      index = this._commands.length + index;
    }

    this._commands[index] = command;
  }

  public toString(close: boolean = false): string {
    let result: string = '';
    for (const command of this._commands) {
      result += command.toString() + ' ';
    }
    if (close) {
      return result + 'Z';
    } else {
      return result.trimEnd();
    }
  }
  public fromString(pathString: string): void {
    this._commands = [];

    const pathArray: IterableIterator<RegExpMatchArray> = pathString
      .replace(/\s*,\s*/g, ' ')
      .matchAll(/[MmLlHhVvCcSsQqTtAaZz][\s.\-\d]*/g);

    for (const commandString of pathArray) {
      const commandArray: string[] = commandString.toString().trim().split(' ');

      switch (commandArray[0]) {
        case 'M':
        case 'm':
          const moveTo: MoveTo = new MoveTo({
            x: parseFloat(commandArray[1]),
            y: parseFloat(commandArray[2])
          },
          false,
            commandArray[0] === 'm'
          );
          this.add(moveTo);
          break;

        case 'L':
        case 'l':
          const lineTo: LineTo = new LineTo({
            x: parseFloat(commandArray[1]),
            y: parseFloat(commandArray[2])
          },
            false,
            commandArray[0] === 'l'
          );
          this.add(lineTo);
          break;

        case 'H':
        case 'h':
          const hLineTo: HLineTo = new HLineTo({
            x: parseFloat(commandArray[1]),
            y: 0
          },
            false,
            commandArray[0] === 'h'
          );
          this.add(hLineTo);
          break;

        case 'V':
        case 'v':
          const vLineTo: VLineTo = new VLineTo({
            x: 0,
            y: parseFloat(commandArray[1])
          },
            false,
            commandArray[0] === 'v'
          );
          this.add(vLineTo);
          break;

        case 'C':
        case 'c':
          const cBezier: CBezier = new CBezier({
            x: parseFloat(commandArray[1]),
            y: parseFloat(commandArray[2])
          },{
            x: parseFloat(commandArray[3]),
            y: parseFloat(commandArray[4])
          },{
            x: parseFloat(commandArray[5]),
            y: parseFloat(commandArray[6])
          },
            false,
            commandArray[0] === 'c'
          );
          this.add(cBezier);
          break;

        case 'S':
        case 's':
          const sCBezier: SCBezier = new SCBezier({
            x: parseFloat(commandArray[1]),
            y: parseFloat(commandArray[2])
          },{
            x: parseFloat(commandArray[3]),
            y: parseFloat(commandArray[4])
          },
            false,
            commandArray[0] === 's'
          );
          this.add(sCBezier);
          break;

        case 'Q':
        case 'q':
          const qBezier: QBezier = new QBezier({
            x: parseFloat(commandArray[1]),
            y: parseFloat(commandArray[2])
          },{
            x: parseFloat(commandArray[3]),
            y: parseFloat(commandArray[4])
          },
            false,
            commandArray[0] === 'q'
          );
          this.add(qBezier);
          break;

        case 'T':
        case 't':
          const sQBezier: SQBezier = new SQBezier({
            x: parseFloat(commandArray[1]),
            y: parseFloat(commandArray[2])
          },
            false,
            commandArray[0] === 't'
          );
          this.add(sQBezier);
          break;

        case 'A':
        case 'a':
          const arc: Arc = new Arc(
            parseFloat(commandArray[1]),
            parseFloat(commandArray[2]),
            parseFloat(commandArray[3]),
            parseFloat(commandArray[4]),
            parseFloat(commandArray[5]), {
            x: parseFloat(commandArray[6]),
            y: parseFloat(commandArray[7])
          },
            false,
            commandArray[0] === 'a'
          );
          this.add(arc);
          break;

        case 'Z':
        case 'z':
          this._commands[this._commands.length - 1].close = true;
          break;
      }
    }
  }
}
